/**
 * Body editor for the conditional_assign step: target field name, the
 * value mode (true / blank / copy-from-field), the source-field slot when
 * copying, and the drop zone for the OR'd gating conditions.
 */
import React, { useCallback } from "react"
import { Radio, Tooltip } from "antd"
import type { RadioChangeEvent } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons"
import { BuilderDropZone } from "shared/ui/Builder"
import { QueryBuilderStore } from "stores/queryBuilderStore"
import { ConditionalAssignStep } from "types/queryBuilder.types"
import SingleFieldSlot from "../SingleFieldSlot.component"
import { StepBodyProps } from "./stepBodyProps"

export interface ConditionalAssignStepBodyProps
  extends StepBodyProps<ConditionalAssignStep> {
  renameStep: QueryBuilderStore["renameTransformStep"]
  setCondMode: QueryBuilderStore["setConditionalValueMode"]
  setCondField: QueryBuilderStore["setConditionalValueField"]
  addCond: QueryBuilderStore["addConditionalCondition"]
  removeCond: QueryBuilderStore["removeConditionalCondition"]
}

const ConditionalAssignStepBody: React.FC<ConditionalAssignStepBodyProps> = ({
  step,
  renameStep,
  setCondMode,
  setCondField,
  addCond,
  removeCond,
}) => {
  const handleAddCondition = useCallback(
    (variable: string) => addCond(step.id, variable),
    [addCond, step.id]
  )
  const handleRemoveCondition = useCallback(
    (variable: string) => removeCond(step.id, variable),
    [removeCond, step.id]
  )

  return (
    <>
      <input
        className="qc-recodes__name qc-mono"
        type="text"
        value={step.target}
        onChange={(e) => renameStep(step.id, e.target.value)}
        placeholder="Target field name (use // for nesting)"
        aria-label="Target field name"
      />
      <div className="qc-step__field-row">
        <span className="qc-step__field-label">
          Set to:
          <Tooltip
            title={
              <>
                The value assigned to the target where the condition
                holds. <code>true</code> uses the auto-emitted{" "}
                <code>&lt;&lt;Boolean//True&gt;&gt;</code> helper field;{" "}
                <code>blank</code> uses the <code>Blank</code> helper
                (sets values back to null); <code>copy from field</code>{" "}
                copies another field's value through.
              </>
            }
            placement="top"
            overlayClassName="dataq-tooltip"
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="qc-step__field-info"
            />
          </Tooltip>
        </span>
        <Radio.Group
          value={step.valueMode}
          onChange={(e: RadioChangeEvent) =>
            setCondMode(
              step.id,
              e.target.value as ConditionalAssignStep["valueMode"]
            )
          }
          size="small"
        >
          <Radio.Button value="true">true</Radio.Button>
          <Radio.Button value="blank">blank</Radio.Button>
          <Radio.Button value="field">copy from field</Radio.Button>
        </Radio.Group>
      </div>
      {step.valueMode === "field" && (
        <div className="qc-step__field-row">
          <span className="qc-step__field-label">Source field:</span>
          <SingleFieldSlot
            value={step.valueField}
            onChange={(v) => setCondField(step.id, v)}
            placeholder="Drag the field to copy from"
          />
        </div>
      )}
      <BuilderDropZone
        label="Where (any of these apply)"
        variables={step.conditions}
        onAdd={handleAddCondition}
        onRemove={handleRemoveCondition}
        emptyHint="Drag fields or answer options that gate the assignment"
        expandParents
      />
    </>
  )
}

export default ConditionalAssignStepBody
